import React, { useState, useCallback } from 'react';
import { Screen, ScreenFormData } from '../../types/screen';
import ScreenSelector from '../../components/Screens/ScreenSelector';
import ScreenView from '../../components/Screens/ScreenView';
import ScreenCreateModal from '../../components/Screens/ScreenCreateModal';
import { toast } from 'react-hot-toast';

const ScreensDashboard: React.FC = () => {
  const [screens, setScreens] = useState<Screen[]>([
    {
      id: '1',
      name: 'Screen 1',
      symbol: 'AAPL',
      timeframe: '1d',
      indicators: [
        { id: '1', name: 'SMA 50', type: 'moving_average', settings: { period: 50 } },
        { id: '2', name: 'RSI', type: 'rsi', settings: { period: 14 } },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '2',
      name: 'Screen 2',
      symbol: 'TSLA',
      timeframe: '4h',
      indicators: [
        { id: '3', name: 'MACD', type: 'macd', settings: {} },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ]);
  
  const [activeScreenId, setActiveScreenId] = useState<string | undefined>(screens[0]?.id);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [screenToClone, setScreenToClone] = useState<Screen | undefined>();
  const [savingScreenId, setSavingScreenId] = useState<string | null>(null);
  
  const activeScreen = screens.find(s => s.id === activeScreenId);
  
  const handleCreateScreen = useCallback(async (data: ScreenFormData) => {
    const newScreen: Screen = {
      id: Date.now().toString(),
      ...data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    setScreens(prev => [...prev, newScreen]);
    setActiveScreenId(newScreen.id);
    setIsCreateModalOpen(false);
    toast.success(`Screen "${newScreen.name}" created successfully`);
  }, []);
  
  const handleCloneScreen = useCallback((screen: Screen) => {
    setScreenToClone(screen);
    setIsCreateModalOpen(true);
  }, []);
  
  const handleCloneSubmit = useCallback(async (data: ScreenFormData) => {
    const clonedScreen: Screen = {
      id: Date.now().toString(),
      ...data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    setScreens(prev => [...prev, clonedScreen]);
    setActiveScreenId(clonedScreen.id);
    setIsCreateModalOpen(false);
    setScreenToClone(undefined);
    toast.success(`Screen "${clonedScreen.name}" cloned successfully`);
  }, []);
  
  const handleUpdateScreen = useCallback(async (screenId: string, updates: Partial<Screen>) => {
    setSavingScreenId(screenId);
    
    setTimeout(() => {
      setScreens(prev =>
        prev.map(screen =>
          screen.id === screenId
            ? { ...screen, ...updates, updatedAt: new Date().toISOString() }
            : screen
        )
      );
      setSavingScreenId(null);
    }, 500);
  }, []);
  
  const handleDeleteScreen = useCallback((screenId: string) => {
    setScreens(prev => {
      const newScreens = prev.filter(s => s.id !== screenId);
      if (activeScreenId === screenId) {
        setActiveScreenId(newScreens[0]?.id);
      }
      return newScreens;
    });
    toast.success('Screen deleted successfully');
  }, [activeScreenId]);
  
  const handleModalClose = useCallback(() => {
    setIsCreateModalOpen(false);
    setScreenToClone(undefined);
  }, []);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      <div className={`
        fixed inset-y-0 left-0 z-40 w-80 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <ScreenSelector
          screens={screens}
          activeScreenId={activeScreenId}
          onSelectScreen={(id) => {
            setActiveScreenId(id);
            setIsSidebarOpen(false);
          }}
          onCreateScreen={() => {
            setScreenToClone(undefined);
            setIsCreateModalOpen(true);
          }}
          onCloneScreen={handleCloneScreen}
          onDeleteScreen={handleDeleteScreen}
        />
      </div>
      
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">
            {activeScreen?.name || 'Screens'}
          </h1>
          <button
            onClick={() => {
              setScreenToClone(undefined);
              setIsCreateModalOpen(true);
            }}
            className="p-2 text-[#3B82F6] hover:text-blue-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        {activeScreen ? (
          <ScreenView
            screen={activeScreen}
            onUpdateScreen={handleUpdateScreen}
            isSaving={savingScreenId === activeScreen.id}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No screen selected</h3>
              <p className="text-sm text-gray-500 mb-4">
                Select a screen from the sidebar or create a new one
              </p>
              <button
                onClick={() => {
                  setScreenToClone(undefined);
                  setIsCreateModalOpen(true);
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#3B82F6] rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New Screen
              </button>
            </div>
          </div>
        )}
      </div>
      
      <ScreenCreateModal
        isOpen={isCreateModalOpen}
        onClose={handleModalClose}
        onSubmit={screenToClone ? handleCloneSubmit : handleCreateScreen}
        screenToClone={screenToClone}
        existingScreens={screens}
      />
    </div>
  );
};

export default ScreensDashboard;

